import { format, isSameDay, parse } from 'date-fns'

const DATE_SEPARATOR = '〜' as const

const formatDate = (date: Date) => format(date, 'yyyy/MM/dd')
const parseDate = (date: string) => parse(date, 'yyyy/MM/dd', new Date())

const formatDateRange = (dates: Date[]) => {
  return `${formatDate(dates[0] as Date)}${DATE_SEPARATOR}${formatDate(
    dates[dates.length - 1] as Date,
  )}`
}

export const getSelectionLabel = (dates: Date[]) => {
  if (dates.length === 1) {
    const dateStr = formatDate(dates[0] as Date)
    return {
      id: dateStr,
      label: dateStr,
    }
  }

  const rangeStr = formatDateRange(dates)
  return {
    id: rangeStr,
    label: rangeStr,
  }
}

const parseDateRange = (dateRangeStr: string): Date[] => {
  const [from, to] = dateRangeStr.split(DATE_SEPARATOR) as [string, string]

  // Fill dates between
  const fromParsed = parseDate(from)
  const toParsed = parseDate(to)
  const dates: Date[] = []
  let currentDate = fromParsed
  while (currentDate <= toParsed) {
    dates.push(currentDate)
    currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)
  }

  return dates
}

export const parseDateSelection = (dateSelectionStr: string): Date[] => {
  if (dateSelectionStr.includes(DATE_SEPARATOR)) {
    return parseDateRange(dateSelectionStr)
  }

  return [parseDate(dateSelectionStr)]
}

/**
 * Group consecutive dates
 */
export const groupConsecutiveDates = (dates: Date[]): Date[][] => {
  // Create a new array of sorted dates
  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime())

  // Create an empty array to store the groups of consecutive dates
  const result: Date[][] = []
  // Create an empty array to store the current group of consecutive dates
  let currentGroup: Date[] = []

  // Iterate through the sorted dates
  for (const date of sortedDates) {
    // If the current group is empty, add the current date to the group and skip the rest of the loop
    if (currentGroup.length === 0) {
      currentGroup.push(date)
      continue
    }

    // Get the date values of the current date and the previous date in the group
    const prev = currentGroup[currentGroup.length - 1] as Date
    const currentDate = date.getDate()
    const currentMonth = date.getMonth()
    const currentYear = date.getFullYear()
    const prevDate = prev.getDate()
    const prevMonth = prev.getMonth()
    const prevYear = prev.getFullYear()

    // Check if the current date is consecutive to the previous date by comparing the date values
    if (
      currentDate === prevDate + 1 &&
      currentMonth === prevMonth &&
      currentYear === prevYear
    ) {
      currentGroup.push(date)
    }
    // Otherwise, the current date is not consecutive to the previous date, so add the current group to the result array and start a new group with the current date
    else {
      result.push(currentGroup)
      currentGroup = [date]
    }
  }

  // If the current group is not empty, add it to the result array
  if (currentGroup.length > 0) {
    result.push(currentGroup)
  }

  // Return the result array
  return result
}

export const filterDates = (fromDates: Date[], targetDates: Date[]): Date[] => {
  return fromDates.filter((v) => !targetDates.some((d) => isSameDay(v, d)))
}
